'use client';

import React, { useLayoutEffect, useMemo } from 'react';
import { deriveBrandTokens } from '../brandTheme';
import { DEFAULT_THEME } from '../constants';

/**
 * Only what this component reads, structurally.
 *
 * The obvious `IRestaurant & { isOpen?: boolean }` (copied from the sibling
 * components, which genuinely need the computed flag) does NOT accept the
 * `Restaurant` CLASS: that carries an `isOpen(day, time)` METHOD, so the
 * intersection collides. Naming the one field used keeps every caller —
 * plain interface, class instance, or SSR-serialised document — assignable.
 */
interface BrandedLocation {
    websiteConfig?: { branding?: { colors?: { primary?: string } } };
}

interface MenuThemeScopeProps {
    restaurant?: BrandedLocation | null;
    className?: string;
    children: React.ReactNode;
}

/**
 * Publishes the restaurant's brand colour as CSS custom properties for every
 * ordering component beneath it.
 *
 * Ordering components read `var(--menu-primary,#dc2626)` (and its hover / foreground /
 * tint siblings) rather than a Tailwind palette class, because the brand is
 * per-restaurant and a palette class cannot be. Setting them in one place is
 * what lets the whole surface change colour without any component knowing
 * which restaurant it is rendering.
 *
 * It writes them in TWO places, and both are load-bearing:
 *
 *  - on the wrapper element, so the correct colour is present in the server
 *    render and at first paint, before any effect has run;
 *  - on `document.documentElement`, because `CartModal` and
 *    `DeliveryAddressDialog` `createPortal` into `document.body`. A portal is
 *    outside this subtree in the DOM, so it inherits nothing from the wrapper
 *    — and an unresolved `var(--menu-primary,#dc2626)` is not a fallback colour, it is
 *    an invalid declaration, so the cart's primary button would render
 *    transparent. The root copy is what those two read.
 *
 * The root properties are removed on unmount so an embedded menu cannot leave
 * a restaurant's brand behind on the host page.
 */
export function MenuThemeScope({ restaurant: locationRecord, className, children }: MenuThemeScopeProps) {
    const tokens = useMemo(
        () =>
            deriveBrandTokens(
                locationRecord?.websiteConfig?.branding?.colors?.primary || DEFAULT_THEME.primary,
                DEFAULT_THEME.primary,
            ),
        [locationRecord?.websiteConfig?.branding?.colors?.primary],
    );

    useLayoutEffect(() => {
        const root = document.documentElement;
        const entries = Object.entries(tokens);
        for (const [name, value] of entries) root.style.setProperty(name, value);
        return () => {
            for (const [name] of entries) root.style.removeProperty(name);
        };
    }, [tokens]);

    return (
        <div className={className} style={tokens as React.CSSProperties}>
            {children}
        </div>
    );
}

export default MenuThemeScope;
